
import { useMemo } from 'react';
import { FilterOption } from '../TableFilters';

interface IoTGateway {
  deviceId: string;
  location: string;
  status: string;
  connectedDevices: number;
  uptimeHours: number;
  cpuUsage: number;
  memoryUsage: number;
  temperatureCelsius: number;
  firmwareVersion: string;
  networkStatus: {
    connectivityType: string;
    signalStrength: number;
    dataTransferred: number;
    lastPingTime: number;
  };
  maintenanceStatus: {
    lastMaintenance: string;
    nextScheduled: string;
    alerts: string[];
  };
}

export const useIoTGatewayFilters = (
  gateways: IoTGateway[],
  activeFilters: Record<string, string[]>
) => {
  // Generate filter options based on available data
  const filterCategories = useMemo(() => {
    // Get unique locations
    const locations = [...new Set(gateways.map(gateway => gateway.location))].map(location => ({
      value: location,
      label: location,
      count: gateways.filter(gateway => gateway.location === location).length
    }));

    // Status options
    const statuses = [...new Set(gateways.map(gateway => gateway.status))].map(status => ({
      value: status,
      label: status,
      count: gateways.filter(gateway => gateway.status === status).length
    }));

    // Connectivity types
    const connectivityTypes = [...new Set(gateways.map(gateway => gateway.networkStatus.connectivityType))].map(type => ({
      value: type,
      label: type,
      count: gateways.filter(gateway => gateway.networkStatus.connectivityType === type).length
    }));

    // Temperature ranges
    const temperatureRanges: FilterOption[] = [
      { value: 'normal', label: 'Normal (<45°C)', count: gateways.filter(g => g.temperatureCelsius < 45).length },
      { value: 'elevated', label: 'Elevated (45-60°C)', count: gateways.filter(g => g.temperatureCelsius >= 45 && g.temperatureCelsius <= 60).length },
      { value: 'high', label: 'High (>60°C)', count: gateways.filter(g => g.temperatureCelsius > 60).length }
    ];

    // Usage (CPU/Memory) ranges
    const usageRanges: FilterOption[] = [
      { value: 'low', label: 'Low Usage (<60%)', count: gateways.filter(g => g.cpuUsage < 60 && g.memoryUsage < 60).length },
      { value: 'medium', label: 'Medium Usage (60-80%)', count: gateways.filter(g => 
        (g.cpuUsage >= 60 && g.cpuUsage <= 80) || (g.memoryUsage >= 60 && g.memoryUsage <= 80)
      ).length },
      { value: 'high', label: 'High Usage (>80%)', count: gateways.filter(g => g.cpuUsage > 80 || g.memoryUsage > 80).length }
    ];

    return [
      { name: 'Location', options: locations },
      { name: 'Status', options: statuses },
      { name: 'ConnectivityType', options: connectivityTypes },
      { name: 'Temperature', options: temperatureRanges },
      { name: 'Usage', options: usageRanges }
    ];
  }, [gateways]);

  // Apply filters to the data
  const filteredGateways = useMemo(() => {
    return gateways.filter(gateway => {
      // Check location filter
      if (activeFilters.Location.length > 0 && !activeFilters.Location.includes(gateway.location)) {
        return false;
      }
      
      // Check status filter
      if (activeFilters.Status.length > 0 && !activeFilters.Status.includes(gateway.status)) {
        return false;
      }
      
      // Check connectivity type filter
      if (activeFilters.ConnectivityType.length > 0 && 
          !activeFilters.ConnectivityType.includes(gateway.networkStatus.connectivityType)) {
        return false;
      }
      
      // Check temperature filter
      if (activeFilters.Temperature.length > 0) {
        const tempMatch = activeFilters.Temperature.some(temp => {
          if (temp === 'normal') return gateway.temperatureCelsius < 45;
          if (temp === 'elevated') return gateway.temperatureCelsius >= 45 && gateway.temperatureCelsius <= 60;
          if (temp === 'high') return gateway.temperatureCelsius > 60;
          return false;
        });
        if (!tempMatch) return false;
      }
      
      // Check usage filter
      if (activeFilters.Usage.length > 0) {
        const usageMatch = activeFilters.Usage.some(usage => {
          if (usage === 'low') return gateway.cpuUsage < 60 && gateway.memoryUsage < 60;
          if (usage === 'medium') return (gateway.cpuUsage >= 60 && gateway.cpuUsage <= 80) || 
                                      (gateway.memoryUsage >= 60 && gateway.memoryUsage <= 80);
          if (usage === 'high') return gateway.cpuUsage > 80 || gateway.memoryUsage > 80;
          return false;
        });
        if (!usageMatch) return false;
      }
      
      return true;
    });
  }, [gateways, activeFilters]);

  return { filterCategories, filteredGateways };
};
